import React, { useEffect, useState } from 'react';
import { getConnectionAttempts, declareAttempt, ignoreAttempt } from '../../services/api';
import { pill } from '../pill';
import Button from '../ui/Button';
import Modal from '../ui/Modal';
import Pager from '../ui/Pager';

const STATUS_LABELS = {
  PENDING: ['En attente', '#fff4e0', '#955d00'],
  DECLARED: ['Déclarée', '#e2f3e8', '#1a7f37'],
  IGNORED: ['Ignorée', '#ececec', '#6b7280'],
  RESOLVED: ['Résolue', '#e3edf9', '#005cc5'],
};

const emptyForm = {
  clientName: '', clientEmail: '', maxUsers: '', uniqueUserWindowDays: '30',
  validFrom: new Date().toISOString().slice(0, 10),
  validUntil: ''
};

/**
 * Serveurs dont la connexion (check-in / report) a été refusée : clé de licence
 * inconnue, désactivée ou expirée. « Déclarer » crée la licence avec la clé que
 * le serveur présente déjà — il s'auto-enregistre au check-in suivant.
 */
export default function ConnectionAttemptsPage() {
  const [result, setResult] = useState({ content: [], totalPages: 0, totalElements: 0 });
  const [showAll, setShowAll] = useState(false);
  const [page, setPage] = useState(0);
  const [declareModal, setDeclareModal] = useState(null); // tentative en cours de déclaration
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [created, setCreated] = useState(null); // licence créée (confirmation)

  const load = () => getConnectionAttempts({ status: showAll ? 'ALL' : 'PENDING', page, size: 50 }).then(setResult);
  useEffect(() => { load(); }, [showAll, page]); // eslint-disable-line
  const attempts = result.content;

  const openDeclare = attempt => {
    setDeclareModal(attempt);
    setForm(emptyForm);
    setError('');
    setCreated(null);
  };

  const closeDeclare = () => {
    setDeclareModal(null);
    setCreated(null);
    load();
  };

  const handleDeclare = async e => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const license = await declareAttempt(declareModal.id, {
        ...form,
        maxUsers: parseInt(form.maxUsers),
        uniqueUserWindowDays: parseInt(form.uniqueUserWindowDays) || 30,
        validFrom: form.validFrom + 'T00:00:00',
        validUntil: form.validUntil + 'T23:59:59',
      });
      setCreated(license);
    } catch (err) {
      setError(err.response?.data?.error || 'Échec de la déclaration.');
    } finally {
      setSaving(false);
    }
  };

  const handleIgnore = async id => { await ignoreAttempt(id); load(); };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
        <h1 style={{ margin: 0 }}>Tentatives de connexion</h1>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          {[false, true].map(all => (
            <Button key={String(all)} size="sm" variant={showAll === all ? 'primary' : 'secondary'} onClick={() => { setShowAll(all); setPage(0); }}>
              {all ? 'Toutes' : 'En attente'}
            </Button>
          ))}
        </div>
      </div>
      <p style={{ color: '#6b7280', fontSize: '0.88rem', margin: '0 0 1.5rem' }}>
        Serveurs refusés au check-in ou au report (clé de licence inconnue, désactivée ou expirée).
        « Déclarer » crée la licence avec la clé présentée : le serveur s'enregistre tout seul à sa
        prochaine connexion, sans reconfiguration.
      </p>

      <div style={{ background: '#ffffff', borderRadius: '12px', overflowX: 'auto' }}>
        <table style={{ width: '100%', minWidth: '1080px', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ color: '#8a8f99', fontSize: '0.8rem', textTransform: 'uppercase', borderBottom: '1px solid #cdcdcd' }}>
              {['Serveur', 'IP', 'Clé présentée', 'Environnement', 'Version', 'Motif du refus', 'Tentatives', 'Dernière', 'Statut', 'Actions'].map(h =>
                <th key={h} style={{ padding: '0.75rem 1rem', textAlign: 'left' }}>{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {attempts.map(a => {
              const [label, bg, color] = STATUS_LABELS[a.status] || [a.status, '#ececec', '#6b7280'];
              return (
                <tr key={a.id} style={{ borderBottom: '1px solid #ececec' }}
                    className="ui-row">
                  <td style={{ ...td, fontWeight: 600 }}>{a.instanceId || <span style={muted}>non déclaré</span>}</td>
                  <td style={{ ...td, fontFamily: 'monospace', color: '#6b7280', fontSize: '0.85rem' }}>{a.callerIp}</td>
                  <td style={{ ...td, fontFamily: 'monospace', fontSize: '0.78rem', color: '#8a8f99' }}>
                    {a.presentedKey || <span style={muted}>aucune</span>}
                  </td>
                  <td style={td}>{a.environment || '—'}</td>
                  <td style={{ ...td, fontSize: '0.85rem', color: '#6b7280' }}>{a.digibrainVersion || '—'}</td>
                  <td style={{ ...td, fontSize: '0.85rem', color: '#b3261e' }}>{a.reason}</td>
                  <td style={{ ...td, fontWeight: 600 }}>{a.attemptCount}</td>
                  <td style={{ ...td, color: '#6b7280', fontSize: '0.82rem' }}>
                    {a.lastAttemptAt ? new Date(a.lastAttemptAt).toLocaleString('fr-FR') : '—'}
                  </td>
                  <td style={td}>
                    <span style={pill(bg, color)}>{label}</span>
                  </td>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>
                    {a.status === 'PENDING' && (
                      <>
                        <Button size="sm" variant="success" onClick={() => openDeclare(a)} disabled={!a.presentedKey}
                                title={a.presentedKey ? '' : 'Aucune clé présentée : configurer package-server.token sur l\'instance'}
                                style={{ marginRight: '0.5rem' }}>
                          Déclarer
                        </Button>
                        <Button size="sm" onClick={() => handleIgnore(a.id)}>Ignorer</Button>
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
            {attempts.length === 0 && (
              <tr><td colSpan={10} style={{ ...td, textAlign: 'center', color: '#8a8f99', padding: '3rem' }}>
                {showAll ? 'Aucune tentative journalisée' : 'Aucune tentative en attente'}
              </td></tr>
            )}
          </tbody>
        </table>
        <div style={{ padding: '0 1rem 1rem' }}>
          <Pager page={page} totalPages={result.totalPages} totalElements={result.totalElements} onPage={setPage} />
        </div>
      </div>

      {declareModal && (
        created ? (
            <Modal title="Licence créée ✔" titleColor="#1a7f37" onClose={closeDeclare}
                   footer={<Button variant="primary" onClick={closeDeclare}>Fermer</Button>}>
              <p style={{ color: '#444444', fontSize: '0.9rem' }}>
                Licence <b>{created.clientName}</b> créée avec la clé
                {' '}<code style={{ fontSize: '0.8rem' }}>{created.licenseKey}</code>.<br />
                Le serveur <b>{declareModal.instanceId || declareModal.callerIp}</b> s'enregistrera
                automatiquement à son prochain check-in — rien d'autre à faire.
              </p>
            </Modal>
          ) : (
            <Modal title="Déclarer le serveur" onClose={closeDeclare} onSubmit={handleDeclare}
                   footer={<>
                     <Button onClick={closeDeclare}>Annuler</Button>
                     <Button type="submit" variant="primary" disabled={saving}>
                       {saving ? 'Création...' : 'Créer la licence'}
                     </Button>
                   </>}>
              <p style={{ color: '#6b7280', fontSize: '0.85rem', margin: '0 0 1rem' }}>
                Crée une licence avec la clé présentée par
                {' '}<b>{declareModal.instanceId || declareModal.callerIp}</b> :
                {' '}<code style={{ fontSize: '0.78rem' }}>{declareModal.presentedKey}</code>
              </p>
              <div style={{ display: 'grid', gap: '0.75rem', gridTemplateColumns: '1fr 1fr' }}>
                <Field label="Nom du client" value={form.clientName} onChange={v => setForm({ ...form, clientName: v })} required />
                <Field label="Email" type="email" value={form.clientEmail} onChange={v => setForm({ ...form, clientEmail: v })} required />
                <Field label="Quota utilisateurs" type="number" value={form.maxUsers} onChange={v => setForm({ ...form, maxUsers: v })} required />
                <Field label="Fenêtre users uniques (jours)" type="number" value={form.uniqueUserWindowDays} onChange={v => setForm({ ...form, uniqueUserWindowDays: v })} required />
                <Field label="Valide à partir du" type="date" value={form.validFrom} onChange={v => setForm({ ...form, validFrom: v })} required />
                <Field label="Valide jusqu'au" type="date" value={form.validUntil} onChange={v => setForm({ ...form, validUntil: v })} required />
              </div>
              {error && <div style={{ color: '#c62828', fontSize: '0.85rem', marginTop: '0.75rem' }}>{error}</div>}
            </Modal>
          )
      )}
    </div>
  );
}

function Field({ label, value, onChange, type = 'text', required }) {
  return (
    <div>
      <label style={{ display: 'block', color: '#6b7280', fontSize: '0.8rem', marginBottom: '4px' }}>{label}</label>
      <input
        type={type}
        value={value}
        onChange={e => onChange(e.target.value)}
        required={required}
        style={{ width: '100%', padding: '0.6rem', background: '#f5f5f5', border: '1px solid #cdcdcd', color: '#444444', borderRadius: '6px', boxSizing: 'border-box' }}
      />
    </div>
  );
}

const td = { padding: '0.85rem 1rem', color: '#444444' };
const muted = { color: '#b0b5bf', fontStyle: 'italic' };
